import { type FC, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Box,
  Flex,
  ListWithPagination,
  Text,
  mainTheme,
  PaginationContext,
  ModalContext,
} from 'dash-ui';

import {
  Amenity,
  GetPropertyByIdQuery,
  useGetAmenitiesQuery,
} from 'gql/generated.graphql';

import AmenityItem from 'containers/properties/PropertyFeaturesAndAmenities/components/AmenityItem/AmenityItem';

const AmenitiesWrapper = styled(Flex)`
  & > div {
    & > div:first-child {
      padding: 0 24px;
    }
  }
  .amenities_container {
    flex-wrap: wrap;
    gap: 16px;
  }
`;

const HeaderFlex = styled(Flex)`
  border-bottom: 1px solid ${mainTheme.colors.n30};
`;

const FeaturesAndAmenities: FC<{
  property: GetPropertyByIdQuery['getPropertyById'];
  disabled?: boolean;
}> = ({ property, disabled = false }) => {
  const { setPayload, toggleModal } = useContext(ModalContext);
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { skip, limit } = getRefetchProps(paginationState);

  const { t: tPropertyPage } = useTranslation('pages', {
    keyPrefix: 'propertyPage',
  });
  const { t: tAmenitiesTable } = useTranslation('tables', {
    keyPrefix: 'amenities',
  });
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });

  const {
    data: result,
    refetch,
    loading,
  } = useGetAmenitiesQuery({
    variables: {
      pagination: { skip, limit },
      sort: [{ fieldName: 'name', sortOrder: 1 }],
      filter: { propertyId: property._id },
    },
  });
  const { data, total } = useMemo(
    () => ({
      data: result?.getAmenities?.collection || [],
      total: result?.getAmenities?.totalCount || 0,
    }),
    [result]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    refetch({
      pagination: { skip, limit },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit]);

  return (
    <Box w="100%">
      <HeaderFlex
        px={6}
        py={4}
        w="100%"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text lg>{tPropertyPage`propertyFeaturesAndAmenities`}</Text>
      </HeaderFlex>
      <AmenitiesWrapper w="100%">
        <ListWithPagination
          listProps={{
            data,
            loading,
            placeholder: tAmenitiesTable('noAmenities') as string,
            containerClassName: 'amenities_container',
            my: 2,
          }}
          paginationProps={{
            label: tAmenitiesTable`paginationLabel` || '',
            previousButtonLabel: tCommonLabels`previous`,
            nextButtonLabel: tCommonLabels`next`,
          }}
        >
          {(amenity: Amenity) => (
            <AmenityItem
              amenity={amenity}
            />
          )}
        </ListWithPagination>
      </AmenitiesWrapper>
    </Box>
  );
};

export default FeaturesAndAmenities;
